import { eventType } from './enums.js';

/**
 * Pairs of (opening event type -> closing event type) whose sequences may legally overlap
 * (event type group Multiple=1, Concurrent=1). Only events within the same pair are matched.
 * Bunker barge at berth: 184 alongside -> 185 casted off.
 * Barge groups 61/62 and 68/69 are also Multiple+Concurrent but are not part of the AOL event
 * filter; add them here ({ 61: 62, 68: 69 }) if they are ever added to the filter.
 */
export const overlappingEventPairs = {
    [eventType.bunkerBargeAlongsideAtBerth]: eventType.bunkerBargeCastedOffAtBerth,
};

/**
 * Collapses overlapping sequences so the state machine only sees the FIRST opening event and the
 * LAST closing event of an overlapping cluster. Intermediate opening/closing events are removed.
 *
 * Cluster rule (interval union, depth counter): +1 on an opening event, -1 on its closing event.
 * A cluster starts when the depth goes 0 -> 1 and ends when it returns to 0.
 * Pairing works on event order only; eventTypeGroupSequenceIdentity / concurrentSequenceNumber are
 * not required (they may be null).
 *
 * Unbalanced data is deliberately NOT repaired, so existing validation still reports the defect:
 *  - a closing event while no sequence is open (more closes than opens) is left untouched;
 *  - a cluster that is never closed (and everything after it) is left untouched.
 *
 * Conservative guards - a cluster is left completely untouched (so validation reports it) when,
 * between its first opening and last closing event (inclusive), ANY event:
 *  - belongs to a different port context than the opener (portClusterCallId when both are
 *    non-null, otherwise portCallId when both are non-null), or
 *  - is an SOSP/EOSP boundary event (a port boundary inside a barge sequence is not an overlap).
 * Cross-cluster pairs such as 184(c1),184(c2),185(c2),185(c1) are therefore never collapsed.
 *
 * Expects evList sorted chronologically. Returns a new array; the input is not mutated.
 */
const boundaryTypes = new Set([eventType.eosp, eventType.sosp]);

const isSameContext = (a, b) => {
    if (a.portClusterCallId != null && b.portClusterCallId != null) return a.portClusterCallId === b.portClusterCallId;
    if (a.portCallId != null && b.portCallId != null) return a.portCallId === b.portCallId;
    return true;
};

const isClusterMergeable = (evList, startIdx, endIdx) => {
    const opener = evList[startIdx];
    for (let i = startIdx; i <= endIdx; i++) {
        if (boundaryTypes.has(evList[i].eventTypeId)) return false;
        if (!isSameContext(opener, evList[i])) return false;
    }
    return true;
};

export const mergeOverlappingEventSequences = (evList, pairs = overlappingEventPairs) => {
    if (!Array.isArray(evList) || evList.length < 3) return evList;

    const removeIndexes = new Set();

    for (const [openTypeKey, closeType] of Object.entries(pairs)) {
        const openType = Number(openTypeKey);
        let depth = 0;
        let clusterIndexes = []; // indexes of opening/closing events in the current open cluster

        evList.forEach((ev, idx) => {
            if (ev.eventTypeId === openType) {
                depth++;
                clusterIndexes.push(idx);
            } else if (ev.eventTypeId === closeType) {
                if (depth === 0) return; // stray close: leave untouched
                depth--;
                clusterIndexes.push(idx);
                if (depth === 0) {
                    // Keep first open and last close; drop everything in between.
                    if (isClusterMergeable(evList, clusterIndexes[0], idx)) {
                        for (let i = 1; i < clusterIndexes.length - 1; i++) {
                            removeIndexes.add(clusterIndexes[i]);
                        }
                    }
                    clusterIndexes = [];
                }
            }
        });
        // Anything left in clusterIndexes is an unclosed cluster: untouched.
    }

    return removeIndexes.size === 0 ? evList : evList.filter((_, idx) => !removeIndexes.has(idx));
};
